import type {
  CmtsHttpClient,
} from "../api/cmtsHttpClient.js";

import {
  CMTS_API,
} from "../api/cmtsApiPaths.js";

import {
  mapExport,
  mapReportDefinition,
  mapTypedReport,
} from "../mappers/reportMappers.js";

import {
  repositoryCall,
} from "./repositoryResult.js";

import {
  mapCollection,
} from "./repositoryCollection.js";

function requiredText(
  value: unknown,
  fieldName: string,
): string {
  const result = String(
    value ?? "",
  ).trim();

  if (!result) {
    throw new Error(
      `${fieldName} is required.`,
    );
  }

  return result;
}

function normalizeExportFormat(
  value: unknown,
): string {
  const format = String(
    value ?? "JSON",
  )
    .trim()
    .toUpperCase();

  if (
    !["CSV", "XLSX", "JSON"].includes(
      format,
    )
  ) {
    throw new Error(
      "Report export format must be CSV, XLSX or JSON.",
    );
  }

  return format;
}

function normalizeMaxRows(
  value: unknown,
): number {
  const requestedMaxRows = Number(
    value ?? 10_000,
  );

  if (
    !Number.isFinite(requestedMaxRows) ||
    requestedMaxRows <= 0
  ) {
    return 10_000;
  }

  return Math.min(
    Math.trunc(requestedMaxRows),
    100_000,
  );
}

function normalizeFiltersJson(
  payload: Record<string, unknown>,
): string {
  if (
    typeof payload.filtersJson ===
      "string" &&
    payload.filtersJson.trim()
  ) {
    /*
     * Ensure provided string is valid JSON.
     */
    try {
      JSON.parse(
        payload.filtersJson,
      );

      return payload.filtersJson;
    } catch {
      throw new Error(
        "Report filtersJson must contain valid JSON.",
      );
    }
  }

  return JSON.stringify(
    payload.filters ?? {},
  );
}

function exportCreatePayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const tenantId = requiredText(
    payload.tenantId,
    "Tenant ID",
  );

  const reportCode = requiredText(
    payload.reportCode ??
    payload.code,
    "Report code",
  );

  const format =
    normalizeExportFormat(
      payload.format ??
      payload.exportFormat,
    );

  const filtersJson =
    normalizeFiltersJson(payload);

  const maxRows =
    normalizeMaxRows(
      payload.maxRows,
    );

  const includeInternalNotes =
    payload.includeInternalNotes === true;

  /*
   * Backend ReportExportCreateRequest expects
   * only these exact fields.
   *
   * Do not send:
   * exportFormat
   * requestedAt
   * code
   * filters object
   */
  return {
    tenantId,
    reportCode,
    format,
    filtersJson,
    maxRows,
    includeInternalNotes,
  };
}

function requiredExportId(
  id: string,
): string {
  const exportId = id.trim();

  if (!exportId) {
    throw new Error(
      "Report export ID is required.",
    );
  }

  return exportId;
}

export function createReportRepository(
  client: CmtsHttpClient,
) {
  return {
    /*
     * Report catalogue
     */
    listCatalog: (
      tenantId?: string,
    ) =>
      repositoryCall(
        async () =>
          mapCollection(
            await client.get<unknown>(
              CMTS_API.reports.catalog,
              {
                query: {
                  tenantId,
                },
              },
            ),
            mapReportDefinition,
          ),
        "CMTS_REPORT_CATALOG_LIST",
      ),

    /*
     * Report definition
     */
    definition: (
      code: string,
      tenantId?: string,
    ) =>
      repositoryCall(
        async () => {
          const reportCode =
            requiredText(
              code,
              "Report code",
            );

          const response =
            await client.get<unknown>(
              CMTS_API.reports.definition(
                reportCode,
              ),
              {
                query: {
                  tenantId,
                },
              },
            );

          return mapReportDefinition(
            response,
          );
        },
        "CMTS_REPORT_DEFINITION_DETAIL",
      ),

    /*
     * Report preview
     */
    preview: (
      payload: Record<string, unknown>,
    ) =>
      repositoryCall(
        async () => {
          const reportCode =
            requiredText(
              payload.reportCode ??
              payload.code ??
              "complaint-register",
              "Report code",
            );

          const response =
            await client.post<unknown>(
              CMTS_API.reports.preview,
              {
                ...payload,
                reportCode,
              },
            );

          return mapTypedReport(
            response,
            reportCode,
          );
        },
        "CMTS_REPORT_PREVIEW",
      ),

    /*
     * Report filter dropdown options
     */
    filterOptions: (
      tenantId?: string,
    ) =>
      repositoryCall(
        async () =>
          mapCollection(
            await client.get<unknown>(
              CMTS_API.reports
                .filterOptions,
              {
                query: {
                  tenantId,
                },
              },
            ),
            (row) => ({
              ...(typeof row ===
                "object" &&
              row !== null &&
              !Array.isArray(row)
                ? row as Record<
                    string,
                    unknown
                  >
                : {}),
            }),
          ),
        "CMTS_REPORT_FILTER_OPTIONS",
      ),

    /*
     * GET /api/cmts/report-exports
     */
    listExports: (
      tenantId?: string,
    ) =>
      repositoryCall(
        async () =>
          mapCollection(
            await client.get<unknown>(
              CMTS_API.reports.exports,
              {
                query: {
                  tenantId,
                },
              },
            ),
            mapExport,
          ),
        "CMTS_REPORT_EXPORTS_LIST",
      ),

    /*
     * POST /api/cmts/report-exports
     */
    createExport: (
      payload: Record<string, unknown>,
    ) =>
      repositoryCall(
        async () => {
          const request =
            exportCreatePayload(
              payload,
            );

          const response =
            await client.post<unknown>(
              CMTS_API.reports.exports,
              request,
            );

          return mapExport(response);
        },
        "CMTS_REPORT_EXPORT_CREATE",
      ),

    /*
     * GET /api/cmts/report-exports/{id}
     */
    exportDetail: (
      id: string,
      tenantId?: string,
    ) =>
      repositoryCall(
        async () => {
          const exportId =
            requiredExportId(id);

          const response =
            await client.get<unknown>(
              CMTS_API.reports
                .exportDetail(
                  exportId,
                ),
              {
                query: {
                  tenantId,
                },
              },
            );

          return mapExport(response);
        },
        "CMTS_REPORT_EXPORT_DETAIL",
      ),

    /*
     * POST /api/cmts/report-exports/{id}/retry
     *
     * Backend receives tenantId from query.
     * No request body is required.
     */
    retryExport: (
      id: string,
      payload: Record<string, unknown>,
    ) =>
      repositoryCall(
        async () => {
          const exportId =
            requiredExportId(id);

          const tenantId =
            requiredText(
              payload.tenantId,
              "Tenant ID",
            );

          const response =
            await client.post<unknown>(
              CMTS_API.reports
                .exportRetry(
                  exportId,
                ),
              {},
              {
                query: {
                  tenantId,
                },
              },
            );

          return mapExport(response);
        },
        "CMTS_REPORT_EXPORT_RETRY",
      ),

    /*
     * POST /api/cmts/report-exports/{id}/cancel
     *
     * Backend receives tenantId from query.
     * No request body is required.
     */
    cancelExport: (
      id: string,
      payload: Record<string, unknown>,
    ) =>
      repositoryCall(
        async () => {
          const exportId =
            requiredExportId(id);

          const tenantId =
            requiredText(
              payload.tenantId,
              "Tenant ID",
            );

          const response =
            await client.post<unknown>(
              CMTS_API.reports
                .exportCancel(
                  exportId,
                ),
              {},
              {
                query: {
                  tenantId,
                },
              },
            );

          return mapExport(response);
        },
        "CMTS_REPORT_EXPORT_CANCEL",
      ),

    /*
     * GET /api/cmts/report-exports/{id}/download
     */
    download: (
      id: string,
      tenantId?: string,
    ) => {
      const exportId =
        requiredExportId(id);

      const resolvedTenantId =
        requiredText(
          tenantId,
          "Tenant ID",
        );

      return client.download(
        CMTS_API.reports
          .exportDownload(
            exportId,
          ),
        {
          query: {
            tenantId:
              resolvedTenantId,
          },
        },
      );
    },
  };
}